import axios from "axios";

const REFRESH_URL = "/auth/refresh_token";
const LOGIN_URL = "/auth/login";

const api = axios.create({
	baseURL: process.env.REACT_APP_BACKEND_URL,
	withCredentials: true,
});

/**
 * Ponte entre os interceptors (que vivem no escopo do módulo) e o React.
 *
 * Os DOIS interceptors precisam existir desde o import de `api`, antes de
 * qualquer componente montar: no React os `useEffect` dos filhos rodam ANTES do
 * efeito do provider de auth, então um interceptor registrado lá chega tarde —
 * a primeira requisição de algumas telas sairia sem `Authorization`, ou um 403
 * inicial não seria repetido, e a tela renderizaria vazia.
 *
 * Mas o tratamento de sessão expirada depende do React (history, setState).
 * Daí esta ponte: o interceptor é global e chama handlers que o `useAuth`
 * registra ao montar. Enquanto ninguém registrou, o interceptor ainda faz a
 * limpeza mínima (token e header), evitando realimentar o ciclo de 403.
 */
let authHandlers = {};

export const setAuthHandlers = (handlers) => {
	authHandlers = handlers || {};
	const registered = authHandlers;
	// Só limpa se ainda for esta a inscrição ativa. `useAuth` é consumido em mais
	// de um lugar; sem essa guarda, o unmount de um consumidor apagaria os
	// handlers registrados por outro.
	return () => {
		if (authHandlers === registered) authHandlers = {};
	};
};

export const clearAuthHandlers = () => {
	authHandlers = {};
};

export const getAuthHandlers = () => authHandlers;

const readToken = () => {
	try {
		const raw = localStorage.getItem("token");
		return raw ? JSON.parse(raw) : null;
	} catch (_) {
		// Token malformado no storage. Como o interceptor agora é global, deixar a
		// exceção subir quebraria TODAS as requisições.
		return null;
	}
};

/** Limpeza que funciona mesmo sem handlers registrados. */
const clearAuthState = () => {
	try {
		localStorage.removeItem("token");
	} catch (_) {
		/* storage indisponível: segue */
	}
	delete api.defaults.headers.Authorization;
};

/** Limpa a sessão e, havendo handler, delega o logout/redirect ao React. */
const failSession = () => {
	clearAuthState();
	if (typeof authHandlers.onSessionExpired === "function") {
		authHandlers.onSessionExpired();
	}
};

// --- Refresh single-flight ---------------------------------------------------
// Várias respostas 403 simultâneas compartilham UMA única chamada ao refresh,
// em vez de dispararem uma cada (o que gerava tempestade de requisições).
let refreshPromise = null;

export const refreshAuthSession = () => {
	if (!refreshPromise) {
		refreshPromise = api
			.post(REFRESH_URL)
			.then(({ data }) => {
				localStorage.setItem("token", JSON.stringify(data.token));
				api.defaults.headers.Authorization = `Bearer ${data.token}`;
				return data;
			})
			.finally(() => {
				refreshPromise = null;
			});
	}
	return refreshPromise;
};

// --- Request interceptor (global) --------------------------------------------
api.interceptors.request.use(
	(config) => {
		const token = readToken();
		if (token) {
			config.headers["Authorization"] = `Bearer ${token}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);

// --- Response interceptor (global) -------------------------------------------
api.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config || {};
		const status = error?.response?.status;
		const url = originalRequest.url || "";

		// O próprio refresh falhou: não há sessão a recuperar. Tratado aqui para
		// não depender de quem o chamou.
		if (url.includes(REFRESH_URL)) {
			failSession();
			return Promise.reject(error);
		}

		// Erro no login é do formulário, não de sessão expirada.
		if (url.includes(LOGIN_URL)) {
			return Promise.reject(error);
		}

		// O backend responde 403 tanto para token expirado quanto para permissão
		// negada (isAuth.ts). Não é preciso distinguir: tentamos UM refresh.
		//  - token expirado   -> refresh OK    -> repete a requisição original
		//  - sessão morta     -> refresh falha -> limpa sessão e volta ao login
		//  - permissão negada -> refresh OK    -> o retry devolve 403 de novo e,
		//                                         com _retry marcado, o erro sobe
		//                                         intacto e a sessão é preservada
		if (status === 403 && !originalRequest._retry) {
			originalRequest._retry = true;
			try {
				const data = await refreshAuthSession();
				originalRequest.headers = {
					...originalRequest.headers,
					Authorization: `Bearer ${data.token}`,
				};
				return api(originalRequest);
			} catch (refreshError) {
				failSession();
				return Promise.reject(refreshError);
			}
		}

		if (status === 401) {
			failSession();
		}

		return Promise.reject(error);
	}
);

export const openApi = axios.create({
	baseURL: process.env.REACT_APP_BACKEND_URL

});

export default api;
